import React from 'react'
import { displayDollar } from '../../../lib/utils/helpers'
import {
  Investment,
  InvestmentStatus_v1,
  RawOpportunity,
  UserDetails,
} from '../../../lib/types/propsType_v1'
import {
  canCancelOngoingInvestment,
  investmentNeedsWalletPayment,
  isOngoingInvestmentInitiated,
  withUpperScaleSuffix,
} from '../dashboard-v2.utils'
import { DashboardV2ProgressStepper, DashboardV2StepPhase } from './DashboardV2ProgressStepper'

export type OngoingInvestmentRow = Investment & RawOpportunity & UserDetails

type OngoingInvestmentsSectionProps = {
  investments: OngoingInvestmentRow[]
  onInvestmentContinue?: (investment: OngoingInvestmentRow) => void
  onInvestmentViewDetails?: (investment: OngoingInvestmentRow) => void
  onInvestmentCancel?: (investment: OngoingInvestmentRow) => void
}

const getInvestmentStepPhases = (
  status: string,
  needsWalletPayment = false,
): {
  request: DashboardV2StepPhase
  payment: DashboardV2StepPhase
  executed: DashboardV2StepPhase
} => {
  switch (status) {
    case InvestmentStatus_v1.INITIATED:
      return { request: 'current', payment: 'upcoming', executed: 'upcoming' }
    case InvestmentStatus_v1.PAYMENT_DONE:
      return { request: 'done', payment: 'done', executed: 'upcoming' }
    case InvestmentStatus_v1.ORDER_PLACED:
      // Wallet payment happens before the order is placed; only legacy unpaid rows still wait on payment.
      return needsWalletPayment
        ? { request: 'done', payment: 'current', executed: 'upcoming' }
        : { request: 'done', payment: 'done', executed: 'current' }
    case InvestmentStatus_v1.REJECTED:
    case InvestmentStatus_v1.CANCELLED:
      return { request: 'done', payment: 'upcoming', executed: 'upcoming' }
    default:
      return { request: 'current', payment: 'upcoming', executed: 'upcoming' }
  }
}

const formatStartedBadge = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  const mon = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
  return `STARTED ${mon} ${d.getDate()}, ${d.getFullYear()}`
}

const investmentFooterMessage = (status: string, needsWalletPayment = false): string => {
  switch (status) {
    case InvestmentStatus_v1.INITIATED:
      return 'Investment initiated — complete next steps'
    case InvestmentStatus_v1.PAYMENT_DONE:
      return 'Payment done — place your order to continue'
    case InvestmentStatus_v1.ORDER_PLACED:
      return needsWalletPayment
        ? 'Order placed — wallet payment required'
        : 'Order placed — awaiting trade execution'
    case InvestmentStatus_v1.REJECTED:
      return 'Investment rejected'
    case InvestmentStatus_v1.CANCELLED:
      return 'Investment cancelled'
    default:
      return 'In progress'
  }
}

const isOpportunityClosedForInvestment = (investment: OngoingInvestmentRow): boolean => {
  const opportunityStatus =
    (
      investment as OngoingInvestmentRow & {
        opportunity_status?: string
        opprotunity_status?: string
      }
    ).opportunity_status ??
    (
      investment as OngoingInvestmentRow & {
        opportunity_status?: string
        opprotunity_status?: string
      }
    ).opprotunity_status

  return opportunityStatus?.toLowerCase() === 'closed'
}

const STEP_LABELS: [string, string, string] = ['Request', 'Payment', 'Execution']

export const OngoingInvestmentsSection: React.FC<OngoingInvestmentsSectionProps> = ({
  investments,
  onInvestmentContinue,
  onInvestmentViewDetails,
  onInvestmentCancel,
}) => {
  return (
    <section
      className='dashboard-v2__ongoing-investments'
      data-testid='dashboard-ongoing-investments'
    >
      <div className='card-head'>
        <h2>Ongoing investments</h2>
      </div>
      {investments.length === 0 ? (
        <p className='dashboard-v2__ongoing-investments-empty'>No investments in progress.</p>
      ) : (
        <div className='dashboard-v2__withdrawal-grid'>
          {investments.map((inv) => {
            const isOpportunityClosed = isOpportunityClosedForInvestment(inv)
            const needsWalletPayment = investmentNeedsWalletPayment(inv.status, inv.metadata)
            const canCancel =
              typeof onInvestmentCancel === 'function' && canCancelOngoingInvestment(inv.status)
            const canContinue =
              typeof onInvestmentContinue === 'function' &&
              inv.opportunity_id !== null &&
              isOngoingInvestmentInitiated(inv.status)
            // Paid from the wallet but the order was never placed: let the investor finish it.
            const canPlaceOrder =
              typeof onInvestmentContinue === 'function' &&
              inv.opportunity_id !== null &&
              inv.status === InvestmentStatus_v1.PAYMENT_DONE &&
              !isOpportunityClosed
            const canViewDetails =
              typeof onInvestmentViewDetails === 'function' &&
              inv.opportunity_id !== null &&
              !isOngoingInvestmentInitiated(inv.status)
            const startedBadge = formatStartedBadge(inv.created_at)
            const phases = getInvestmentStepPhases(inv.status, needsWalletPayment)
            const amountDisplay = withUpperScaleSuffix(
              displayDollar(String(inv.amount), 'USD', 2, false),
            )
            const title = inv.opportunity_title || 'Investment'

            return (
              <article key={inv.id} className='dashboard-v2__withdrawal-card'>
                <div className='dashboard-v2__withdrawal-card__head'>
                  <div className='dashboard-v2__withdrawal-card__titles'>
                    <h3 className='dashboard-v2__withdrawal-card__title'>{title}</h3>
                    <p className='dashboard-v2__withdrawal-card__subscription'>
                      Amount: <strong>{amountDisplay}</strong>
                    </p>
                  </div>
                  {startedBadge ? (
                    <span className='dashboard-v2__withdrawal-card__date-badge'>
                      {startedBadge}
                    </span>
                  ) : null}
                </div>

                <DashboardV2ProgressStepper
                  request={phases.request}
                  payment={phases.payment}
                  executed={phases.executed}
                  labels={STEP_LABELS}
                />

                <div className='dashboard-v2__withdrawal-card__footer'>
                  <span className='dashboard-v2__withdrawal-card__footer-status'>
                    {isOpportunityClosed
                      ? 'The opportunity is closed for investment'
                      : investmentFooterMessage(inv.status, needsWalletPayment)}
                  </span>
                  {canCancel || canContinue || canPlaceOrder || canViewDetails ? (
                    <div className='dashboard-v2__withdrawal-card__footer-actions'>
                      {canContinue ? (
                        <button
                          type='button'
                          className='dashboard-v2__withdrawal-card__footer-link'
                          disabled={isOpportunityClosed}
                          onClick={() => onInvestmentContinue(inv)}
                          data-testid={`continue-ongoing-investment-${inv.id}`}
                        >
                          Continue
                        </button>
                      ) : null}
                      {canPlaceOrder ? (
                        <button
                          type='button'
                          className='dashboard-v2__withdrawal-card__footer-link'
                          onClick={() => onInvestmentContinue(inv)}
                          data-testid={`place-order-ongoing-investment-${inv.id}`}
                        >
                          Place order
                        </button>
                      ) : null}
                      {canViewDetails ? (
                        <button
                          type='button'
                          className='dashboard-v2__withdrawal-card__footer-link'
                          onClick={() => onInvestmentViewDetails(inv)}
                          data-testid={`view-ongoing-investment-${inv.id}`}
                        >
                          View details
                        </button>
                      ) : null}
                      {canCancel ? (
                        <button
                          type='button'
                          className='dashboard-v2__withdrawal-card__footer-cancel'
                          onClick={() => onInvestmentCancel(inv)}
                          data-testid={`cancel-ongoing-investment-${inv.id}`}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
